import type { TypographyProps } from "@mui/material";
import type { ThemeOptions } from "@mui/material/styles";
import { capitalize } from "remeda";

const fontFamily = "var(--font-sans)";

const fontSize = {
  default: 16,
};

const fontWeights = {
  regular: "var(--font-weight-regular)",
  semiBold: "var(--font-weight-semibold)",
};
type FontWeight = keyof typeof fontWeights;

const fontSizes = ["extraSmall", "small", "normal", "large", "extraLarge", "huge", "enormous", "screenTitle"] as const;
type FontSize = (typeof fontSizes)[number];

const getType = (size: FontSize, weight: FontWeight): React.CSSProperties => ({
  fontFamily,
  fontSize: `var(--text-${size})`,
  fontWeight: fontWeights[weight],
  letterSpacing: "-0.02em",
  textDecoration: "none",
});

const typography = Object.keys(fontWeights).reduce(
  (acc, _weight) => {
    const weight = _weight as FontWeight;
    for (const size of fontSizes) {
      acc[`${capitalize(weight)}${capitalize(size)}`] = getType(size, weight);
    }
    return acc;
  },
  {} as Record<`${Capitalize<FontWeight>}${Capitalize<FontSize>}`, React.CSSProperties>,
);

export default {
  htmlFontSize: fontSize.default,
  fontFamily,
  fontWeightRegular: fontWeights.regular,
  fontWeightMedium: fontWeights.semiBold,
  fontWeightBold: fontWeights.semiBold,
  ...typography,
  NormalLink: getType("normal", "semiBold"),
} as ThemeOptions["typography"];

export const variantMapping: TypographyProps["variantMapping"] = {
  RegularSmall: "p",
  RegularNormal: "p",
  RegularLarge: "p",
  RegularExtraLarge: "p",
  RegularHuge: "p",
  RegularEnormous: "p",
  SemiBoldExtraSmall: "p",
  SemiBoldSmall: "p",
  SemiBoldNormal: "p",
  SemiBoldLarge: "p",
  SemiBoldExtraLarge: "p",
  SemiBoldHuge: "p",
  SemiBoldEnormous: "p",
  SemiBoldScreenTitle: "h1",
  NormalLink: "p",
};

interface GigProTypographyVariants {
  RegularSmall: React.CSSProperties;
  RegularNormal: React.CSSProperties;
  RegularLarge: React.CSSProperties;
  RegularExtraLarge: React.CSSProperties;
  RegularHuge: React.CSSProperties;
  RegularEnormous: React.CSSProperties;
  SemiBoldExtraSmall: React.CSSProperties;
  SemiBoldSmall: React.CSSProperties;
  SemiBoldNormal: React.CSSProperties;
  SemiBoldLarge: React.CSSProperties;
  SemiBoldExtraLarge: React.CSSProperties;
  SemiBoldHuge: React.CSSProperties;
  SemiBoldEnormous: React.CSSProperties;
  SemiBoldScreenTitle: React.CSSProperties;
  NormalLink: React.CSSProperties;
}

declare module "@mui/material/styles" {
  interface TypographyVariants extends GigProTypographyVariants {}

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions extends GigProTypographyVariants {}
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    // disable MUI variants
    h1: false;
    h2: false;
    h3: false;
    h4: false;
    h5: false;
    h6: false;
    subtitle1: false;
    subtitle2: false;
    body1: false;
    body2: false;
    caption: false;
    overline: false;
    button: false;
    // enable custom variants
    RegularSmall: true;
    RegularNormal: true;
    RegularLarge: true;
    RegularExtraLarge: true;
    RegularHuge: true;
    RegularEnormous: true;
    SemiBoldExtraSmall: true;
    SemiBoldSmall: true;
    SemiBoldNormal: true;
    SemiBoldLarge: true;
    SemiBoldExtraLarge: true;
    SemiBoldHuge: true;
    SemiBoldEnormous: true;
    SemiBoldScreenTitle: true;
    NormalLink: true;
  }
}
